import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Bell,
  Check,
  CheckCheck,
  Mail,
  Trash2,
  X,
  ArrowDownCircle,
  ArrowUpCircle,
  AlertTriangle,
  ShieldAlert,
  Info,
} from 'lucide-react';
import {
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkNotificationUnreadMutation,
  useMarkAllNotificationsReadMutation,
  useDeleteNotificationMutation,
  useClearAllNotificationsMutation,
} from '../../store/api/notificationsApi';
import useNotificationStream from '../../hooks/useNotificationStream';

// Compact relative time — the popover has no room for full timestamps.
const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Icon per event family, falling back to severity. Payments point in, payouts out.
const iconFor = ({ eventType = '', severity = 'info' }) => {
  if (eventType.startsWith('payment.')) return ArrowDownCircle;
  if (eventType.startsWith('withdrawal.') || eventType.startsWith('payout.')) return ArrowUpCircle;
  if (eventType.startsWith('security.')) return ShieldAlert;
  if (severity === 'critical') return ShieldAlert;
  if (severity === 'warning') return AlertTriangle;
  return Info;
};

const NotificationsMenu = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  // Bumped once a minute while the popover is open so "just now" ages correctly.
  const [, setClockTick] = useState(0);
  const wrapRef = useRef(null);

  const { data, isLoading, isError, refetch } = useGetNotificationsQuery(undefined, {
    // The feed is kept current by SSE; this only covers a tab that was open
    // before the stream connected.
    refetchOnMountOrArgChange: true,
  });

  const [markRead] = useMarkNotificationReadMutation();
  const [markUnread] = useMarkNotificationUnreadMutation();
  const [markAllRead, { isLoading: isMarkingAll }] = useMarkAllNotificationsReadMutation();
  const [removeNotification] = useDeleteNotificationMutation();
  const [clearAll, { isLoading: isClearing }] = useClearAllNotificationsMutation();

  const items = useMemo(() => data?.items || [], [data]);
  const unreadCount = data?.unreadCount || 0;

  // Toast newly arrived notifications so a merchant on another page still sees
  // the payment land. Kept to a short list of severities so routine reports
  // don't interrupt.
  const handleIncoming = useCallback((notification) => {
    const line = notification.title;
    if (notification.severity === 'success') toast.success(line);
    else if (notification.severity === 'critical') toast.error(line);
    else toast(line, { icon: '🔔' });
  }, []);

  useNotificationStream({ onNotification: handleIncoming });

  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setClockTick((t) => t + 1), 60000);
    return () => clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const visible = useMemo(
    () => (showUnreadOnly ? items.filter((n) => !n.read) : items),
    [items, showUnreadOnly]
  );

  // Opening a notification marks it read, which is what people expect. The
  // per-row toggle is the undo for when they wanted to keep it flagged.
  const openNotification = (notification) => {
    if (!notification.read) markRead(notification.id);
    setOpen(false);
    if (notification.link) navigate(notification.link);
  };

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div className="notif-dropdown" ref={wrapRef}>
      <button
        className="icon-btn notification-btn"
        aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notif-badge" aria-hidden="true">
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div className="dropdown-menu notif-menu" role="dialog" aria-label="Notifications">
          <div className="notif-head">
            <div className="notif-head-title">
              <strong>Notifications</strong>
              {unreadCount > 0 && <span className="notif-head-count">{unreadCount} unread</span>}
            </div>
            <div className="notif-head-actions">
              <button
                type="button"
                className={`notif-filter ${showUnreadOnly ? 'active' : ''}`}
                onClick={() => setShowUnreadOnly((v) => !v)}
              >
                {showUnreadOnly ? 'All' : 'Unread'}
              </button>
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="notif-tool notif-head-tool"
                  onClick={() => markAllRead()}
                  disabled={isMarkingAll}
                  title="Mark all as read"
                  aria-label="Mark all as read"
                >
                  <CheckCheck size={15} />
                </button>
              )}
              {items.length > 0 && (
                <button
                  type="button"
                  className="notif-tool notif-head-tool danger"
                  onClick={() => clearAll()}
                  disabled={isClearing}
                  title="Clear all"
                  aria-label="Clear all notifications"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>

          <div className="notif-list">
            {isLoading && <div className="notif-state">Loading…</div>}

            {isError && !isLoading && (
              <div className="notif-state">
                Could not load notifications.
                <button type="button" className="notif-retry" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            )}

            {!isLoading && !isError && visible.length === 0 && (
              <div className="notif-empty">
                <Bell size={22} />
                <p>{showUnreadOnly ? 'Nothing unread' : "You're all caught up"}</p>
                <span>Notifications about payments and payouts will appear here.</span>
              </div>
            )}

            {visible.map((notification) => {
              const Icon = iconFor(notification);
              return (
                <div
                  key={notification.id}
                  className={`notif-item sev-${notification.severity} ${notification.read ? '' : 'unread'}`}
                >
                  <button
                    type="button"
                    className="notif-item-main"
                    onClick={() => openNotification(notification)}
                  >
                    <span className="notif-item-icon">
                      <Icon size={16} />
                    </span>
                    <span className="notif-item-text">
                      <span className="notif-item-title">{notification.title}</span>
                      {notification.body && (
                        <span className="notif-item-body">{notification.body}</span>
                      )}
                      <span className="notif-item-time">{relativeTime(notification.createdAt)}</span>
                    </span>
                    {!notification.read && <span className="notif-item-dot" aria-hidden="true" />}
                  </button>

                  <div className="notif-item-tools">
                    {/* One button that toggles, rather than two that appear and
                        disappear — the control stays in the same place as the
                        row's state changes. */}
                    <button
                      type="button"
                      className="notif-tool"
                      title={notification.read ? 'Mark as unread' : 'Mark as read'}
                      aria-label={notification.read ? 'Mark as unread' : 'Mark as read'}
                      onClick={() =>
                        notification.read
                          ? markUnread(notification.id)
                          : markRead(notification.id)
                      }
                    >
                      {notification.read ? <Mail size={14} /> : <Check size={14} />}
                    </button>
                    <button
                      type="button"
                      className="notif-tool"
                      title="Dismiss"
                      aria-label="Dismiss notification"
                      onClick={() => removeNotification(notification.id)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsMenu;
